import React, { useState , useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import swal from '@sweetalert/with-react';

import { sortArrayOfObjects, loadSpecificData, calculateConflicts } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';
import { buildLabelsById } from '../utils/studentLabelHelpers';
import { getLabelColorClass } from '../utils/labelColorUtils';

import '../../../css/hsms/edit_modals.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle, faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import CourseRequests from './reusable-components/CourseRequests';
import { useConflictActions } from '../store/magnetboardStore';

const mergeSavedStudentIntoList = (student_list = [], old_student_id, saved_student) => {
	const old_student_id_string = `${old_student_id}`;
	const new_student_id_string = `${saved_student.student_id}`;
	const original_student_index = student_list.findIndex((student_info) => `${student_info.student_id}` === old_student_id_string);

	if(old_student_id_string === new_student_id_string)
	{
		return student_list.map((student_info) => (
			`${student_info.student_id}` === old_student_id_string ? saved_student : student_info
		));
	}

	const next_student_list = student_list.filter((student_info) => (
		`${student_info.student_id}` !== old_student_id_string && `${student_info.student_id}` !== new_student_id_string
	));

	if(original_student_index === -1) return [...next_student_list, saved_student];

	const safe_insert_index = Math.min(original_student_index, next_student_list.length);
	return [
		...next_student_list.slice(0, safe_insert_index),
		saved_student,
		...next_student_list.slice(safe_insert_index),
	];
};

export default function EditStudentData({ dataID, toggleEditData, schoolInfo, scheduleInfo, students, courses, teachers, departments, sections, labels, setDatabaseData }) {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState({});
	
	const [studentLabelResults, setStudentLabelResults] = useState([]);
	const [teacherRestrictionResults, setTeacherRestrictionResults] = useState([]);
	const [studentRestrictionResults, setStudentRestrictionResults] = useState([]);
	
	const [addStudentLabelOpen, setAddStudentLabelOpen] = useState(false);
	const [addTeacherRestrictionOpen, setAddTeacherRestrictionOpen] = useState(false);
	const [addStudentRestrictionOpen, setAddStudentRestrictionOpen] = useState(false);
	
	const [changesMade, setChangesMade] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const labelsByID = useMemo(() => buildLabelsById(labels), [labels]);

	const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();
	
	const grades = (students.length === 0) ? [{display:'5', value:'5'},{display:'6', value:'6'},{display:'7', value:'7'},{display:'8', value:'8'},{display:'9', value:'9'},{display:'10', value:'10'},{display:'11', value:'11'},{display:'12', value:'12'}] : students.reduce((result, student) => {
		const student_grade = student.grade;
		if(result.findIndex(option => option.value === student_grade) === -1) result.push({display:student_grade, value:student_grade});
		return result;
	}, []);
	const sorted_grades = sortArrayOfObjects(grades, 'value', 'number', 'asc');

	const genders = [{value:'M', display:'Male'}, {value:'F', display:'Female'}, {value:'O', display: 'Other'}];
	const toggleAddNewConnection = (connection_type) => {
		if(connection_type === 'student_label')
		{
			setAddStudentLabelOpen(!addStudentLabelOpen);
			setStudentLabelResults(addStudentLabelOpen ? [] : sortArrayOfObjects([...labels], 'label', 'text', 'asc'));
		}
		else if(connection_type === 'teacher_restriction')
		{
			setAddTeacherRestrictionOpen(!addTeacherRestrictionOpen);
			setTeacherRestrictionResults(addTeacherRestrictionOpen ? [] : [...teachers]);
		}
		else if(connection_type === 'student_restriction')
		{
			setAddStudentRestrictionOpen(!addStudentRestrictionOpen);
			setStudentRestrictionResults(addStudentRestrictionOpen ? [] : [...students]);
		}
	}
	
	const fuzzySearch = (e, dataType) => {
		const search_value = e.target.value;
		
		if(dataType === 'teachers')
		{
			const fuse = new Fuse(teachers, {
				keys: ['name', 'first_name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const teacher_results = search_value.trim().length === 0 ? [...teachers] : results.map(result => result.item);
			
			setTeacherRestrictionResults(teacher_results);
		}
		else if(dataType === 'students')
		{
			const fuse = new Fuse(students, {
				keys: ['first_name', 'last_name', 'student_code'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const student_results = (search_value.trim().length === 0 ? [...students] : results.map(result => result.item))
				.sort((a, b) => {
					const a_last_name = `${a.last_name || ''}`.toLowerCase();
					const b_last_name = `${b.last_name || ''}`.toLowerCase();
					if(a_last_name < b_last_name) return -1;
					if(a_last_name > b_last_name) return 1;
					const a_first_name = `${a.first_name || ''}`.toLowerCase();
					const b_first_name = `${b.first_name || ''}`.toLowerCase();
					if(a_first_name < b_first_name) return -1;
					if(a_first_name > b_first_name) return 1;
					return 0;
				});
			
			setStudentRestrictionResults(student_results);
		}
		else if(dataType === 'labels')
		{
			const sorted_labels = sortArrayOfObjects([...labels], 'label', 'text', 'asc');
			const fuse = new Fuse(sorted_labels, {
				keys: ['label'],
				threshold: .3
			});

			const results = fuse.search(search_value);
			const label_results = search_value.trim().length === 0 ? sorted_labels : results.map(result => result.item);

			setStudentLabelResults(label_results);
		}
	}
	
	const updateData = (change_type, data_id, data_key, new_value = null, permanently_remove = false, sub_data_key = null, args = {}) => {
		const newData = {...data};

		if (change_type === 'add') {
			if (data_key === 'student_requests') {
				newData[data_key].push({
					group_id: data_id,
					priority: '1',
					course_requests: [{
						course_id: new_value, 
						matched:'0', 
						priority:'1', 
						is_inclusion:'0', 
						data_origin:'2', 
						is_deleted:'0',
						order: '1'
					}]
				});
			} else if (data_key === 'course_requests') {
				const request_index = newData['student_requests'].findIndex(request => request.group_id === data_id);
				if (request_index !== -1) {
					newData['student_requests'][request_index]['course_requests'].push({
						course_id: new_value,
						order: String(args.order),
						matched:'0',
						is_inclusion:'0',
						data_origin:'2',
						is_deleted:'0'
					});
				}
			} else {
				newData[data_key].push(data_id);
			}
		} else if(change_type === 'change') {
			if (data_key === 'student_requests') {
				const request_index = newData['student_requests'].findIndex(request => request.group_id === data_id);
				if (request_index !== -1) {
					newData['student_requests'][request_index][sub_data_key] = new_value;
				}
			} else if (data_key === 'course_requests') {
				const request_index = newData['student_requests'].findIndex(request => request.group_id === data_id);
				if (request_index !== -1) {
					newData['student_requests'][request_index]['course_requests'] = new_value.map(course_request => {
						return {
							course_id: course_request.course_id,
							order: course_request.order,
							matched: course_request.matched || '0',
							is_inclusion: course_request.is_inclusion || '0',
							data_origin: course_request.data_origin || '2',
							is_deleted: course_request.is_deleted || '0',
							request_icon: course_request.request_icon || null,
							color: course_request.color || null,
						};
					});
				}
			} else {
				newData[data_key] = data_id;
			}
		} else if (change_type === 'remove') {
			if (data_key === 'student_requests') {
				const request_index = newData[data_key].findIndex(request => request.group_id === data_id);
				if (request_index !== -1) {
					newData[data_key].splice(request_index, 1);
				}
			} else {
				newData[data_key].splice(data_id, 1);
			}
		}
		
		if(change_type !== 'add')
		{
			setAddStudentLabelOpen(false);
			setAddTeacherRestrictionOpen(false);
			setAddStudentRestrictionOpen(false);
			setStudentLabelResults([]);
			setTeacherRestrictionResults([]);
			setStudentRestrictionResults([]);
		}
		
		setData({...newData});

		if (changesMade === false) {
			setChangesMade(true);
		}
	}
	
	const handleExitWithoutSaving = async () => {
		let exit_without_saving = true;
		
		if(changesMade)
		{
			const options =  {
				title: "Exit without saving?",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'blue-btn'
					},
				},
				content: (
					<div>
						<p>You have changes that have not been saved. Do you really want to exit without saving?</p>
					</div>
				)
			}
		
			exit_without_saving = await swal(options);
		}
		
		if(exit_without_saving) toggleEditData();
	}
	
	const updateStudent = async () => {
		// Make sure student data is trimmed
		const data_to_save = {
			...data,
			student_code: data.student_code ? data.student_code.trim() : '',
			first_name: data.first_name ? data.first_name.trim() : '',
			last_name: data.last_name ? data.last_name.trim() : '',
		};

		const student_code = data_to_save.student_code;
		const first_name = data_to_save.first_name;
		const last_name = data_to_save.last_name;
		const grade = data_to_save.grade;
		
		if(!student_code || !first_name || !last_name || !grade)
		{
			const options =  {
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div className='text-align-left'>
						<p>You are missing data! Make sure you have the following data before saving:</p>
						<ul>
							<li>Student Code</li>
							<li>First Name</li>
							<li>Last Name</li>
							<li>Gender</li>
							<li>Grade</li>
						</ul>
					</div>
				)
			}
		
			swal(options);
		}
		else
		{
			setIsSaving(true);

			try
			{
				const response_data = await saveDataToDatabase({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'student', data_to_save:data_to_save});

				if(!response_data?.new_data_id)
				{
					throw new Error('Student save did not return a student id.');
				}

				// change data student id to new database id
				const saved_student_id = response_data.new_data_id;
				let saved_student = {
					...data_to_save,
					student_id: saved_student_id,
					is_deleted: '0',
				};

				const refreshed_student = await loadSpecificData({
					data_type:'student',
					data_id:saved_student_id,
					data_version_id:schoolInfo.current_data_version_id,
					schedule_version_id:schoolInfo.current_schedule_version_id,
					school_id:schoolInfo.school_id,
				});

				if(refreshed_student && !refreshed_student.msg) saved_student = refreshed_student;

				setDatabaseData((current_students) => {
					const current_students_array = Array.isArray(current_students) ? current_students : [];
					return mergeSavedStudentIntoList(current_students_array, dataID, saved_student);
				});
				setIsSaving(false);
				toggleEditData(null);

				////////////////////////
				/// Update conflicts ///
				////////////////////////
				sections.reduce((results, section) => {
					const section_student_list = section.student_list;
					if(section_student_list.includes(saved_student.student_id)) results.push(section.section_id);
					return results;
				}, []);

				calculateConflicts({
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					match_percent: true,
				}).then(conflicts_response => {
					const conflicts_list = conflicts_response.conflicts;
					setConflicts([...conflicts_list]);
					setMatchPercent(conflicts_response.match_percent);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
				});
			}
			catch(e)
			{
				console.log(e.response || e);
				setIsSaving(false);
				swal({
					title: "Unable to Save",
					icon:"warning",
					buttons: {
						cancel: {
							text: "Okay",
							value: false,
							visible: true,
							className: 'blue-btn'
						}
					},
					content: (
						<div>
							<p>We couldn&rsquo;t save this student. Please try again.</p>
						</div>
					)
				});
			}
		}
	}
	
	/////////////////////////////
	///// FOCUS ON INPUT ////////
	/////////////////////////////
	useEffect(() => {
		if(addStudentLabelOpen) document.getElementById("search-student-label-input").focus();
	}, [addStudentLabelOpen]);
	
	useEffect(() => {
		if(addTeacherRestrictionOpen) document.getElementById("search-teacher-restriction-input").focus();
	}, [addTeacherRestrictionOpen]);
	
	useEffect(() => {
		if(addStudentRestrictionOpen) document.getElementById("search-student-restriction-input").focus();
	}, [addStudentRestrictionOpen]);
	
	
	/////////////////////////
	///// DO ON LOAD ////////
	/////////////////////////
	useEffect(() => {
		if(dataID && !isSaving)
		{
			loadSpecificData({data_type:'student', data_id:dataID, data_version_id:schoolInfo.current_data_version_id, schedule_version_id:schoolInfo.current_schedule_version_id, school_id:schoolInfo.school_id}).then(specific_data => {
				if(!specific_data || specific_data.msg) return;

				setDatabaseData((current_students) => {
					const current_students_array = Array.isArray(current_students) ? current_students : [];
					return mergeSavedStudentIntoList(current_students_array, dataID, specific_data);
				});
				// Create a deep copy to prevent local changes from affecting the global students array
				setData(JSON.parse(JSON.stringify(specific_data)));
				setIsLoading(false);
			});
		}
		else
		{
			setData({new: true, first_name: null, gender: null, grade: null, is_special_ed: "0", last_name: null, state_id: null, student_code: null, student_id: null, student_requests: [], student_labels:[], student_restrictions: [], teacher_restrictions: []});
			setIsLoading(false);
		}

		}, [dataID, schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id, setDatabaseData]);
	
	//console.log({dataID, toggleEditData, scheduleStructureData, students, courses, teachers, sections});
	//console.log(data);

	const student_course_sections = sections.filter(section => section.student_list.includes(dataID));
	
	return (
		<div className='modal-screen'>
			<div className='modal-screen-content'>
				{!isSaving &&
					<>
					<span className="data-modal-close" onClick={handleExitWithoutSaving}>&times;</span>
					<div className="modal-save-btn" onClick={updateStudent}><FontAwesomeIcon icon={faSave}/>Save Student</div>
					</>
				}
				<div className='full-width'>
					<div className='edit-data-content'>
						<FontAwesomeIcon className='edit-data-main-head-icon' icon={faEdit}/>
						<h1 className='edit-data-main-header'>{dataID ? 'Edit' : 'Add New'} Student</h1>
						{isSaving &&
							<div className='dashboard-database-message-container'>
								<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
								<div>Saving...</div>
							</div>
						}
						{isLoading &&
							<div className='dashboard-database-message-container'>
								<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
							</div>
						}
						{(!isLoading && !isSaving) &&
							<form className='edit-data-form'>
								<div className='edit-data-grid-33' style={{gridTemplateColumns:'200px 1fr 1fr'}}>
									<div>
										<p className='modal-label'>Student ID/Code:</p>
										<input type="text" className='edit-data-input' defaultValue={data.student_code} onChange={(e) => updateData('change', e.target.value, 'student_code')}/>
									</div>
									<div>
										<p className='modal-label'>Last Name:</p>
										<input type="text" className='edit-data-input' defaultValue={data.last_name} onChange={(e) => updateData('change', e.target.value, 'last_name')}/>
									</div>
									<div>
										<p className='modal-label'>First Name:</p>
										<input type="text" className='edit-data-input' defaultValue={data.first_name} onChange={(e) => updateData('change', e.target.value, 'first_name')}/>
									</div>
								</div>
								<div className='edit-data-grid-50'>
									<div>
										<div className='data-modal-heading'>Grade</div>
										<div id="student-grade-container">
											{sorted_grades.map((grade_info, index) => {
												return (
													<div className={`option-btn ${grade_info.value === data.grade ? 'option-btn-selected' : ''}`} key={index} onClick={() => updateData('change', grade_info.value, 'grade')}>{grade_info.display}</div>
												)	
											})}
										</div>
									</div>
									<div>
										<div className='data-modal-heading'>Gender</div>
										<div id="student-gender-type-container">
											{genders.map((gender_info, index) => {
												return (
													<div className={`option-btn ${gender_info.value === data.gender ? 'option-btn-selected' : ''}`} key={index} onClick={() => updateData('change', gender_info.value, 'gender')}>{gender_info.display}</div>
												)	
											})}
										</div>
									</div>
								</div>
								<div>
									<div className='data-modal-heading'>Course Requests</div>
									<CourseRequests
										schoolId={schoolInfo.school_id}
										scheduleVersionId={schoolInfo.current_schedule_version_id}
										scheduleInfo={scheduleInfo}
										studentRequests={data.student_requests}
										courses={courses}
										sections={sections}
										labels={labels}
										studentLabels={data.student_labels}
										onUpdateData={updateData}
										studentCourseSections={student_course_sections}
									/>
								</div>
								<div>
									<div className='data-modal-heading'>Special Ed Flag</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div style={{width:'100%',textAlign:'left'}}>
											<div>Does this student have an IEP?</div>
											<div className='dark-gray-text'>Answer "Yes" if this is a speciel ed student</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.is_special_ed === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'is_special_ed')}>No</div><div className={`toggle-with-text-btn-optn ${data.is_special_ed === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'is_special_ed')}>Yes</div>
										</div>
									</div>
								</div>
								<div>
									<div className='data-modal-heading'>Student Teams/Labels</div>
									<div className='dark-gray-text edit-data-explanation-text'>Add student labels</div>
									<div className='edit-data-list'>
										{data.student_labels.length === 0 &&
											<div className='edit-data-no-data-container'>No student teams/labels!</div>
										}
										{data.student_labels.map((student_label, index) => {
											const label_info = labelsByID[student_label.label_id];
											if(!label_info) return null;
											return (
												<div className='edit-section-section-label' key={index} style={{padding:'8px 12px'}}>
													<div className={`create-labels-icon ${getLabelColorClass(label_info)}`}></div>
													<div>{label_info.label}</div>
													<FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => updateData('remove', index, 'student_labels')}/>
												</div>
											)	
										})}
									</div>
									<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('student_label')}>
										<FontAwesomeIcon icon={faPlusCircle}/>
										<div>Add Student Team/Label</div>
									</div>
									{addStudentLabelOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-student-label-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'labels')} placeholder='Search for teams/labels'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('student_label')}/>
											</div>
											{studentLabelResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{studentLabelResults.map((label, i) => {
														const label_id = label.label_id;
														const label_info = labelsByID[label_id];
														if(!label_info) return null;
														const student_has_label = data.student_labels.findIndex(student_label => student_label.label_id === label_id) !== -1;

															return (
																<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'auto 1fr auto'}}>
																	<div className={`create-labels-icon ${getLabelColorClass(label_info)}`}></div>
																	<div>{label_info.label}</div>
																	<div>
																	{student_has_label
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', label, 'student_labels')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
								</div>
								<div>
									<div className='data-modal-heading'>Student-Teacher Restrictions</div>
									<div className='dark-gray-text edit-data-explanation-text'>Add a teacher this student cannot be assigned to</div>
									<div className='edit-data-list'>
										{data.teacher_restrictions.length === 0 &&
											<div className='edit-data-no-data-container'>No restricted teachers!</div>
										}
										{data.teacher_restrictions.map((teacher_id, index) => {
											const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
											
											return (
												<div className='table-row-inv edit-data-teacher-restriction-row' key={index}>
													<div>{teacher_info.name}, {teacher_info.first_name}</div>
													<div>
														{teacher_info.departments.map((department_id, index) => {
															const department_index = departments.findIndex(department => department.department_id === department_id);
															const department = (department_index !== -1) ? departments[department_index].department : 'No department';
															
															return(
																<div className='dashboard-department medium-text' key={index}>{department}</div>
															)	
														})}
													</div>
													<div className="remove-data" onClick={() => updateData('remove', index, 'teacher_restrictions')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)	
										})}
									</div>
									<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('teacher_restriction')}>
										<FontAwesomeIcon icon={faPlusCircle}/>
										<div>Add Teacher Restriction</div>
									</div>
									{addTeacherRestrictionOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-teacher-restriction-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'teachers')} placeholder='Search for teacher'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('teacher_restriction')}/>
											</div>
											{teacherRestrictionResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{teacherRestrictionResults.map((teacher, i) => {
														const teacher_selected = data.teacher_restrictions.includes(teacher.teacher_id);
														return (
															<div className='search-add-search-result' key={i}>
																<div>{teacher.name}, {teacher.first_name}</div>
																<div>
																	{teacher_selected
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', teacher.teacher_id, 'teacher_restrictions')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
								</div>
								<div>
									<div className='data-modal-heading'>Student-Student Restrictions</div>
									<div className='dark-gray-text edit-data-explanation-text'>Add a student this student cannot be in the same classes with</div>
									<div className='edit-data-list'>
										{data.student_restrictions.length === 0 &&
											<div className='edit-data-no-data-container'>No restricted students!</div>
										}
										{data.student_restrictions.map((student_id, index) => {
											const student_info = students.find(student => student.student_id === student_id);
											
											return (
												<div className='table-row-inv edit-data-student-restriction-row' key={index}>
													<div>{student_info.last_name}, {student_info.first_name}</div>
													<div className="remove-data" onClick={() => updateData('remove', index, 'student_restrictions')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)	
										})}
									</div>
									<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('student_restriction')}>
										<FontAwesomeIcon icon={faPlusCircle}/>
										<div>Add Student Restriction</div>
									</div>
									{addStudentRestrictionOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-student-restriction-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'students')} placeholder='Search for student'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('student_restriction')}/>
											</div>
											{studentRestrictionResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{studentRestrictionResults.map((student, i) => {
														const student_selected = data.student_restrictions.includes(student.student_id);
														return (
															<div className='search-add-search-result' key={i}>
																<div>
																	{student.last_name}, {student.first_name}
																	{student.student_code ? <span className='dark-gray-text'> ({student.student_code})</span> : ''}
																</div>
																<div>
																	{student_selected
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', student.student_id, 'student_restrictions')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
								</div>
							</form>
						}
					</div>
				</div>
			</div>
		</div>
	);
}
